/*************************************************************
 * Enhanced AppContext.jsx - Global State Management with Better Configuration Integration
 *************************************************************/

import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { api } from '../api/reportingApi.js';

const AppContext = createContext();

export const AppProvider = ({ children, userData }) => {
    const [state, setState] = useState({
        currentTab: 'dashboard',
        filters: {
            dateRange: '', // Will be set from configuration
            project: '',
            station: '',
            defectType: '',
            modelAgreement: '',
            confidence: 0,
            sortBy: 'timestamp'
        },
        loading: false,
        error: null,
        notifications: [],
        preferences: {
            theme: localStorage.getItem('multireports-theme') || 'light',
            itemsPerPage: parseInt(localStorage.getItem('multireports-itemsPerPage')) || 20,
            autoRefresh: localStorage.getItem('multireports-autoRefresh') === 'true',
            refreshInterval: parseInt(localStorage.getItem('multireports-refreshInterval')) || 30,
            default_date_range: localStorage.getItem('multireports-default_date_range') || 'week',
            show_confidence_scores: localStorage.getItem('multireports-show_confidence_scores') !== 'false',
            email_notifications: localStorage.getItem('multireports-email_notifications') !== 'false',
            includeManualImages: localStorage.getItem('multireports-includeManualImages') === 'true',
            workingHoursStart: localStorage.getItem('multireports-workingHoursStart') || '08:00',
            workingHoursEnd: localStorage.getItem('multireports-workingHoursEnd') || '18:00',
            workingDays: JSON.parse(localStorage.getItem('multireports-workingDays') || '["monday","tuesday","wednesday","thursday","friday"]')
        },
        // Configuration state
        configuration: {
            notifications: {
                email_enabled: false,
                sms_enabled: false,
                app_enabled: true,
                light_tower_enabled: false,
                email: '',
                phone: '',
                defect_notifications: true,
                station_offline_notifications: true,
                session_completed_notifications: false,
                daily_summary_enabled: false
            },
            alertRules: [
                {
                    id: 1,
                    name: 'High Defect Rate Alert',
                    type: 'defect_rate',
                    threshold: 10,
                    enabled: true,
                    conditions: { timeframe: '1h', min_samples: 10 }
                },
                {
                    id: 2,
                    name: 'Station Offline Alert',
                    type: 'station_offline',
                    threshold: 5,
                    enabled: true,
                    conditions: { timeframe: '5m' }
                },
                {
                    id: 3,
                    name: 'Low Model Confidence',
                    type: 'low_confidence',
                    threshold: 70,
                    enabled: true,
                    conditions: { timeframe: '30m', min_samples: 5 }
                }
            ],
            isLoaded: false,
            lastLoaded: null
        },
        user: userData
    });

    // Load configuration from backend on initialization
    useEffect(() => {
        console.log('[AppContext] Initializing configuration loading...');
        loadConfiguration();
    }, []);

    // Apply theme when preferences change
    useEffect(() => {
        if (state.preferences.theme && window.applyTheme) {
            console.log(`[AppContext] Applying theme: ${state.preferences.theme}`);
            window.applyTheme(state.preferences.theme);
        }
    }, [state.preferences.theme]);

    // Set default date range in filters once configuration is loaded
    useEffect(() => {
        if (state.configuration.isLoaded && !state.filters.dateRange && state.preferences.default_date_range) {
            console.log(`[AppContext] Setting default date range to filters: ${state.preferences.default_date_range}`);
            setState(prev => ({
                ...prev,
                filters: {
                    ...prev.filters,
                    dateRange: prev.preferences.default_date_range
                }
            }));
        }
    }, [state.configuration.isLoaded, state.preferences.default_date_range, state.filters.dateRange]);

    const loadConfiguration = useCallback(async () => {
        try {
            console.log('[AppContext] Loading configuration from backend...');
            
            // Load all configuration data in parallel
            const [preferencesResult, notificationsResult, alertRulesResult] = await Promise.allSettled([
                api.getPreferences(),
                api.getNotificationSettings(),
                api.getAlertRules()
            ]);

            console.log('[AppContext] Configuration load results:', {
                preferences: preferencesResult.status,
                notifications: notificationsResult.status,
                alertRules: alertRulesResult.status
            });

            const updates = {};

            // Update preferences if loaded successfully
            if (preferencesResult.status === 'fulfilled' && preferencesResult.value?.success) {
                const prefs = preferencesResult.value.data;
                console.log('[AppContext] Loaded preferences from backend:', prefs);
                
                updates.preferences = {
                    ...state.preferences,
                    ...prefs
                };

                // Update localStorage with new preferences
                Object.keys(prefs).forEach(key => {
                    if (typeof prefs[key] === 'object') {
                        localStorage.setItem(`multireports-${key}`, JSON.stringify(prefs[key]));
                    } else {
                        localStorage.setItem(`multireports-${key}`, prefs[key]);
                    }
                });
                
                console.log('[AppContext] Updated localStorage with backend preferences');
            } else {
                console.log('[AppContext] Using localStorage preferences (backend unavailable)');
            }

            // Update notifications if loaded successfully
            if (notificationsResult.status === 'fulfilled' && notificationsResult.value?.success) {
                const notifications = notificationsResult.value.data;
                console.log('[AppContext] Loaded notification settings:', notifications);
                
                updates.configuration = {
                    ...state.configuration,
                    notifications: notifications
                };
            } else {
                console.log('[AppContext] Using default notification settings');
            }

            // Update alert rules if loaded successfully
            if (alertRulesResult.status === 'fulfilled' && alertRulesResult.value?.success) {
                const alertRules = alertRulesResult.value.data;
                console.log('[AppContext] Loaded alert rules:', alertRules);
                
                updates.configuration = {
                    ...updates.configuration || state.configuration,
                    alertRules: alertRules,
                    isLoaded: true,
                    lastLoaded: new Date()
                };
            } else {
                console.log('[AppContext] Using default alert rules');
                updates.configuration = {
                    ...updates.configuration || state.configuration,
                    isLoaded: true,
                    lastLoaded: new Date()
                };
            }

            // Apply all updates
            if (Object.keys(updates).length > 0) {
                console.log('[AppContext] Applying configuration updates:', Object.keys(updates));
                setState(prev => ({
                    ...prev,
                    ...updates
                }));
            }

            console.log('[AppContext] Configuration loading completed');

        } catch (error) {
            console.error('[AppContext] Error loading configuration:', error);
            
            // Mark configuration as loaded even if failed, so app can continue
            setState(prev => ({
                ...prev,
                configuration: {
                    ...prev.configuration,
                    isLoaded: true,
                    lastLoaded: new Date()
                }
            }));
            
            addNotification({
                type: 'warning',
                message: 'Could not load some configuration settings, using defaults',
                duration: 3000
            });
        }
    }, [state.preferences, state.configuration]);

    const updateFilters = useCallback((newFilters) => {
        console.log('[AppContext] Updating filters:', {
            from: state.filters,
            to: newFilters,
            changed: Object.keys(newFilters).filter(key => state.filters[key] !== newFilters[key])
        });
        setState(prev => ({
            ...prev,
            filters: { ...prev.filters, ...newFilters }
        }));
    }, [state.filters]);

    // Utility: include/exclude manual images when building filters
    const withManualImagesPref = useCallback((filters = {}) => {
        const include = state.preferences.includeManualImages === true;
        const result = { ...filters, includeManualImages: include };
        
        console.log(`[AppContext] withManualImagesPref: ${include}`, {
            original: filters,
            result: result
        });
        
        return result;
    }, [state.preferences.includeManualImages]);

    // Utility: page size from preferences
    const defaultPageSize = useCallback(() => {
        const size = Number(state.preferences.itemsPerPage) || 20;
        console.log(`[AppContext] defaultPageSize: ${size}`);
        return size;
    }, [state.preferences.itemsPerPage]);

    const setCurrentTab = useCallback((tab) => {
        console.log(`[AppContext] Switching from ${state.currentTab} to ${tab}`);
        setState(prev => ({ ...prev, currentTab: tab }));
    }, [state.currentTab]);

    const setLoading = useCallback((loading) => {
        console.log(`[AppContext] Setting loading: ${loading}`);
        setState(prev => ({ ...prev, loading }));
    }, []);

    const setError = useCallback((error) => {
        console.error('[AppContext] Setting error:', error);
        setState(prev => ({ ...prev, error }));
    }, []);

    const addNotification = useCallback((notification) => {
        const id = Date.now() + Math.random();
        const newNotification = { ...notification, id };
        console.log('[AppContext] Adding notification:', newNotification);
        setState(prev => ({
            ...prev,
            notifications: [...prev.notifications, newNotification]
        }));

        setTimeout(() => {
            setState(prev => ({
                ...prev,
                notifications: prev.notifications.filter(n => n.id !== id)
            }));
        }, notification.duration || 5000);
    }, []);

    const removeNotification = useCallback((id) => {
        console.log(`[AppContext] Removing notification: ${id}`);
        setState(prev => ({
            ...prev,
            notifications: prev.notifications.filter(n => n.id !== id)
        }));
    }, []);

    const updatePreferences = useCallback(async (newPreferences, saveToBackend = false) => {
        console.log('[AppContext] Updating preferences:', {
            newPreferences, 
            saveToBackend,
            currentPreferences: state.preferences
        });
        
        const updatedPreferences = { ...state.preferences, ...newPreferences };
        
        setState(prev => ({
            ...prev,
            preferences: updatedPreferences
        }));

        // Update localStorage
        Object.keys(newPreferences).forEach(key => {
            if (typeof newPreferences[key] === 'object') {
                localStorage.setItem(`multireports-${key}`, JSON.stringify(newPreferences[key]));
            } else {
                localStorage.setItem(`multireports-${key}`, newPreferences[key]);
            }
        });
        
        console.log('[AppContext] Updated localStorage with preferences');

        // Save to backend if requested
        if (saveToBackend) {
            try {
                await api.savePreferences(updatedPreferences);
                console.log('[AppContext] Preferences saved to backend successfully');
                
                addNotification({
                    type: 'success',
                    message: 'Preferences saved successfully',
                    duration: 3000
                });
            } catch (error) {
                console.error('[AppContext] Error saving preferences to backend:', error);
                addNotification({
                    type: 'danger',
                    message: 'Failed to save preferences: ' + error.message,
                    duration: 5000
                });
            }
        }
    }, [state.preferences]);

    // Configuration management methods
    const updateNotificationSettings = useCallback(async (newSettings, saveToBackend = false) => {
        console.log('[AppContext] Updating notification settings:', { newSettings, saveToBackend });
        
        const updatedSettings = { ...state.configuration.notifications, ...newSettings };
        
        setState(prev => ({
            ...prev,
            configuration: {
                ...prev.configuration,
                notifications: updatedSettings
            }
        }));

        if (saveToBackend) {
            try {
                await api.saveNotificationSettings(updatedSettings);
                console.log('[AppContext] Notification settings saved successfully');
                
                addNotification({
                    type: 'success',
                    message: 'Notification settings saved successfully',
                    duration: 3000
                });
            } catch (error) {
                console.error('[AppContext] Error saving notification settings:', error);
                addNotification({
                    type: 'danger',
                    message: 'Failed to save notification settings: ' + error.message,
                    duration: 5000
                });
            }
        }
    }, [state.configuration.notifications]);

    const updateAlertRules = useCallback(async (newRules, saveToBackend = false) => {
        console.log('[AppContext] Updating alert rules:', { 
            rulesCount: newRules.length, 
            saveToBackend,
            enabledRules: newRules.filter(r => r.enabled).length
        });
        
        setState(prev => ({
            ...prev,
            configuration: {
                ...prev.configuration,
                alertRules: newRules,
                lastSaved: saveToBackend ? new Date() : prev.configuration.lastSaved
            }
        }));

        if (saveToBackend) {
            try {
                await api.saveAlertRules(newRules);
                console.log('[AppContext] Alert rules saved successfully');
                
                addNotification({
                    type: 'success',
                    message: 'Alert rules saved successfully',
                    duration: 3000
                });
            } catch (error) {
                console.error('[AppContext] Error saving alert rules:', error);
                addNotification({
                    type: 'danger',
                    message: 'Failed to save alert rules: ' + error.message,
                    duration: 5000
                });
            }
        }
    }, []);

    const addAlertRule = useCallback((newRule) => {
        const ruleWithId = {
            ...newRule,
            id: newRule.id || Date.now()
        };
        
        console.log('[AppContext] Adding alert rule:', ruleWithId);
        
        setState(prev => ({
            ...prev,
            configuration: {
                ...prev.configuration,
                alertRules: [...prev.configuration.alertRules, ruleWithId]
            }
        }));
    }, []);

    const removeAlertRule = useCallback(async (ruleId, deleteFromBackend = false) => {
        console.log(`[AppContext] Removing alert rule: ${ruleId}, deleteFromBackend: ${deleteFromBackend}`);
        
        setState(prev => ({
            ...prev,
            configuration: {
                ...prev.configuration,
                alertRules: prev.configuration.alertRules.filter(rule => rule.id !== ruleId)
            }
        }));

        if (deleteFromBackend) {
            try {
                await api.deleteAlertRule(ruleId);
                console.log('[AppContext] Alert rule deleted from backend');
                
                addNotification({
                    type: 'success',
                    message: 'Alert rule deleted successfully',
                    duration: 3000
                });
            } catch (error) {
                console.error('[AppContext] Error deleting alert rule:', error);
                addNotification({
                    type: 'danger',
                    message: 'Failed to delete alert rule: ' + error.message,
                    duration: 5000
                });
            }
        }
    }, []);

    const reloadConfiguration = useCallback(async () => {
        console.log('[AppContext] Manually reloading configuration...');
        await loadConfiguration();
    }, [loadConfiguration]);

    // Export configuration for backup
    const exportConfiguration = useCallback(async () => {
        try {
            console.log('[AppContext] Exporting configuration...');
            const result = await api.exportConfiguration();
            if (result.success) {
                const dataStr = JSON.stringify(result.data, null, 2);
                const dataBlob = new Blob([dataStr], { type: 'application/json' });
                const url = URL.createObjectURL(dataBlob);
                
                const link = document.createElement('a');
                link.href = url;
                link.download = `analytics-config-${new Date().toISOString().split('T')[0]}.json`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
                
                addNotification({
                    type: 'success',
                    message: 'Configuration exported successfully',
                    duration: 3000
                });
                
                console.log('[AppContext] Configuration exported successfully');
            }
        } catch (error) {
            console.error('[AppContext] Error exporting configuration:', error);
            addNotification({
                type: 'danger',
                message: 'Failed to export configuration: ' + error.message,
                duration: 5000
            });
        }
    }, []);

    // Import configuration from backup
    const importConfiguration = useCallback(async (configData) => {
        try {
            console.log('[AppContext] Importing configuration...', configData);
            const result = await api.importConfiguration(configData);
            if (result.success) {
                await loadConfiguration(); // Reload from backend
                
                addNotification({
                    type: 'success',
                    message: 'Configuration imported successfully',
                    duration: 3000
                });
                
                console.log('[AppContext] Configuration imported successfully');
            }
        } catch (error) {
            console.error('[AppContext] Error importing configuration:', error);
            addNotification({
                type: 'danger',
                message: 'Failed to import configuration: ' + error.message,
                duration: 5000
            });
        }
    }, [loadConfiguration]);

    // Check if alert rules should trigger (utility method)
    const checkAlertConditions = useCallback((data) => {
        const triggeredAlerts = [];
        
        const enabledRules = state.configuration.alertRules.filter(rule => rule.enabled);
        console.log(`[AppContext] Checking ${enabledRules.length} enabled alert rules against data:`, data);
        
        enabledRules.forEach(rule => {
            let shouldTrigger = false;
            
            switch (rule.type) {
                case 'defect_rate':
                    if (data.defectRate && data.defectRate > rule.threshold) {
                        shouldTrigger = true;
                    }
                    break;
                case 'low_confidence':
                    if (data.confidence && data.confidence < rule.threshold) {
                        shouldTrigger = true;
                    }
                    break;
                case 'station_offline':
                    if (data.stationStatus === 'offline') {
                        shouldTrigger = true;
                    }
                    break;
                default:
                    break;
            }
            
            if (shouldTrigger) {
                console.log(`[AppContext] Alert rule triggered: ${rule.name} (${rule.type})`);
                triggeredAlerts.push(rule);
            }
        });
        
        console.log(`[AppContext] ${triggeredAlerts.length} alerts triggered`);
        return triggeredAlerts;
    }, [state.configuration.alertRules]);

    // Debug logging for state changes
    useEffect(() => {
        console.log('[AppContext] State updated:', {
            currentTab: state.currentTab,
            loading: state.loading,
            filtersSet: Object.keys(state.filters).filter(key => state.filters[key]).length,
            configLoaded: state.configuration.isLoaded,
            alertRulesCount: state.configuration.alertRules.length,
            enabledAlertRules: state.configuration.alertRules.filter(r => r.enabled).length,
            preferencesKeys: Object.keys(state.preferences)
        });
    }, [state]);

    const value = {
        state,
        // Core methods
        updateFilters,
        setCurrentTab,
        setLoading,
        setError,
        addNotification,
        removeNotification,
        updatePreferences,
        // Configuration methods
        updateNotificationSettings,
        updateAlertRules,
        addAlertRule,
        removeAlertRule,
        reloadConfiguration,
        exportConfiguration,
        importConfiguration,
        checkAlertConditions,
        loadConfiguration,
        // Utility methods
        withManualImagesPref,
        defaultPageSize,
    };

    return (
        <AppContext.Provider value={value}>
            {children}
        </AppContext.Provider>
    );
};

export const useAppContext = () => {
    const context = useContext(AppContext);
    if (!context) {
        throw new Error('useAppContext must be used within AppProvider');
    }
    return context;
};